import { Inject, Injectable } from "@angular/core";
import { AppEventId } from "../../../../loader-factory/src/event-bus/app-event.model";
import { AppHostService } from "../app-host.service";
import { APP_CONFIG } from "../config";
import { AppConfig } from "../config/config.model";

/** A service used to interact with the parent iframe */
@Injectable({ providedIn: "root" })
export class AppParentService {
  constructor(
    @Inject(APP_CONFIG) private readonly config: AppConfig,
    private readonly appHostService: AppHostService
  ) {}

  /** Force the display of the bot in the parent container */
  public setBotVisible(visible: boolean): void {
    this.appHostService.send({ eventId: AppEventId.BOT_TOGGLED, data: visible });

    if (visible) {
      this.notifyOpened();
      return;
    }

    this.notifyClosed();
  }

  /** Remove this bot from the parent container */
  public removeBot(): void {
    this.appHostService.send({ eventId: AppEventId.REMOVE, data: null });
  }

  /** Request the parent container to display a popup with an image denoted by the passed URL */
  public showImagePopup(url: string): void {
    this.appHostService.send({ eventId: AppEventId.IMAGE, data: url });
  }

  /** Notify the host that the chatbot has been opened. */
  public notifyOpened(): void {
    this.appHostService.send({ eventId: AppEventId.OPENED, data: null });
  }

  /** Notify the host that the chatbot has been closed. */
  public notifyClosed(): void {
    this.appHostService.send({ eventId: AppEventId.CLOSED, data: null });
  }

  /** Notify the host that the user has engaged with the chatbot for the first time. */
  public notifyUserEngaged(): void {
    this.appHostService.send({ eventId: AppEventId.USER_ENGAGED, data: null });
  }

  /** Notify the host that the user has sent a message to the chatbot. */
  public notifyMessageSent(): void {
    this.appHostService.send({ eventId: AppEventId.MESSAGE_SENT, data: null });
  }

  /** Notify the host that the chatbot has produced a response message. */
  public notifyMessageReceived(): void {
    this.appHostService.send({ eventId: AppEventId.MESSAGE_RECEIVED, data: null });
  }

  /** Notify the host that the user clicked a link rendered in a bot message. */
  public notifyClickLink(href: string): void {
    this.appHostService.send({ eventId: AppEventId.CLICK_LINK, data: href });
  }

  /** Request the host to open a link (http, https, mailto or tel) from the host page. */
  public notifyOpenLink(href: string): void {
    this.appHostService.send({ eventId: AppEventId.OPEN_LINK, data: href });
  }

  /** Request the parent container to change iFrame dimensions */
  public updateHostDimensions(): void {
    if (this.config.hostDimensions) {
      this.appHostService.send({
        eventId: AppEventId.UPDATE_HOST_DIMENSIONS,
        data: this.config.hostDimensions,
      });
    }
  }

  /** Request the host page to begin tracking drag movement from these pointer coordinates. */
  public sendDragStart(): void {
    this.appHostService.send({
      eventId: AppEventId.DRAG_START,
      data: null,
    });
  }

  public getLocalStorageItem(key: string): void {
    this.appHostService.sendLocalStorageEvent("get_" + key, null);
  }

  public setLocalStorageItem(key: string, data: unknown): void {
    this.appHostService.sendLocalStorageEvent("set_" + key, data);
  }
}
